import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, ArrowLeft, Save, Package } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { companyService } from '@/services/companyService';
import { decodeHtmlEntities } from '@/lib/utils';

interface Product {
  id: number;
  created_at: string;
  product_id: string;
  sku_id: string;
  product_type_code: string;
  category_code: string;
  brand: string;
  sku_status: string;
  warehouse: string;
  sku_name: string;
  sku_name_chi: string;
  sku_name_sc: string;
  sku_short_description: string;
  sku_short_description_chi: string;
  sku_short_description_sc: string;
  sku_long_description: string;
  sku_long_description_chi: string;
  sku_long_description_sc: string;
  manufactured_country: string;
  original_price: number | null;
  selling_price: number | null;
  packing_spec_eng: string;
  packing_spec_chi: string;
  packing_spec_sc: string;
  packing_height: number | null;
  packing_length: number | null;
  packing_depth: number | null;
  packing_dimension_unit: string;
  weight: number | null;
  weight_unit: string;
  packing_box_type: string;
  invisible_flag: string;
  barcode: string;
  fine_print_eng: string;
  fine_print_chi: string;
  fine_print_sc: string;
  oversea_delivery: string;
}

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isProductInfoOpen, setIsProductInfoOpen] = useState(true);
  const [isSkuListOpen, setIsSkuListOpen] = useState(true);
  const [isSkuInfoOpen, setIsSkuInfoOpen] = useState(true);
  const [isPackagingOpen, setIsPackagingOpen] = useState(true);
  const [isSkuDetailOpen, setIsSkuDetailOpen] = useState(true);
  const [productTypeCodes, setProductTypeCodes] = useState<Array<{ code: string; description: string }>>([]);
  const [categoryCodeDisplay, setCategoryCodeDisplay] = useState<string>('');
  const [averageCost, setAverageCost] = useState<number | null>(null);
  const [costId, setCostId] = useState<number | null>(null);
  const [standardCommission, setStandardCommission] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const productId = parseInt(id!, 10);
      if (isNaN(productId)) {
        throw new Error('Invalid product ID');
      }

      const company = await companyService.getCurrentCompany();

      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .eq('company_id', company ? Number(company.id) : -1)
        .single();

      if (error) throw error;
      setProduct(data);

      // 查询 category_code 对应的描述信息和佣金率
      if (data?.category_code) {
        const { data: category, error: categoryError } = await supabase
          .from('categories')
          .select('category_code, category_name, category_brand_name, standard_delivery_commission')
          .eq('category_code', data.category_code)
          .single();

        if (!categoryError && category) {
          const description = category.category_brand_name || category.category_name || '';
          setCategoryCodeDisplay(description ? `${data.category_code} - ${description}` : data.category_code);
          setStandardCommission(category.standard_delivery_commission);
        } else {
          setCategoryCodeDisplay(data.category_code);
          setStandardCommission(null);
        }
      }

      // 从当前产品的 product_type_code 字段获取逗号分隔的字符串，并分割为多个 codes
      if (data?.product_type_code) {
        // 按逗号分割 product_type_code 字符串
        const codes = data.product_type_code
          .split(',')
          .map(code => code.trim())
          .filter(code => code.length > 0);

        if (codes.length > 0) {
          // 查询 categories 表获取每个 code 的完整信息
          const { data: categories, error: categoriesError } = await supabase
            .from('categories')
            .select('category_code, category_name, category_brand_name')
            .in('category_code', codes);

          if (!categoriesError && categories) {
            // 构建 product_type_codes 数组，包含 code 和描述
            const codesWithDescription = codes.map(code => {
              const category = categories.find(c => c.category_code === code);
              const description = category?.category_brand_name || category?.category_name || '';
              return {
                code: code,
                description: description
              };
            });
            setProductTypeCodes(codesWithDescription);
          } else {
            // 如果查询 categories 失败，至少显示 code
            setProductTypeCodes(codes.map(code => ({ code, description: '' })));
          }
        }
      }

      // 查询 product_costs 表获取 average_cost（使用 company_sku_key）
      if (company && data?.sku_id) {
        const companyIdNum = Number(company.id);
        const companySkuKey = `${companyIdNum}-${data.sku_id}`;
        const { data: costData, error: costError } = await supabase
          .from('product_costs')
          .select('id, average_cost')
          .eq('company_sku_key', companySkuKey)
          .eq('company_id', companyIdNum)
          .order('average_cost_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!costError && costData) {
          setAverageCost(costData.average_cost);
          setCostId(costData.id);
        } else {
          setAverageCost(null);
          setCostId(null);
        }
      }
    } catch (error: any) {
      toast.error('獲取產品資料失敗: ' + error.message);
      navigate('/product-overview/management');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!product || !id) return;

    setSaving(true);
    try {
      const productId = parseInt(id, 10);
      if (isNaN(productId)) {
        throw new Error('Invalid product ID');
      }

      const { error } = await supabase
        .from('products')
        .update({
          product_id: product.product_id,
          sku_id: product.sku_id,
          product_type_code: product.product_type_code,
          category_code: product.category_code,
          brand: product.brand,
          sku_status: product.sku_status,
          warehouse: product.warehouse,
          sku_name: product.sku_name,
          sku_name_chi: product.sku_name_chi,
          sku_name_sc: product.sku_name_sc,
          sku_short_description: product.sku_short_description,
          sku_short_description_chi: product.sku_short_description_chi,
          sku_short_description_sc: product.sku_short_description_sc,
          sku_long_description: product.sku_long_description,
          sku_long_description_chi: product.sku_long_description_chi,
          sku_long_description_sc: product.sku_long_description_sc,
          manufactured_country: product.manufactured_country,
          original_price: product.original_price,
          selling_price: product.selling_price,
          packing_spec_eng: product.packing_spec_eng,
          packing_spec_chi: product.packing_spec_chi,
          packing_spec_sc: product.packing_spec_sc,
          packing_height: product.packing_height,
          packing_length: product.packing_length,
          packing_depth: product.packing_depth,
          packing_dimension_unit: product.packing_dimension_unit,
          weight: product.weight,
          weight_unit: product.weight_unit,
          packing_box_type: product.packing_box_type,
          invisible_flag: product.invisible_flag,
          barcode: product.barcode,
          fine_print_eng: product.fine_print_eng,
          fine_print_chi: product.fine_print_chi,
          fine_print_sc: product.fine_print_sc,
          oversea_delivery: product.oversea_delivery
        })
        .eq('id', productId);

      if (error) throw error;

      // 保存 average_cost 到 product_costs 表
      if (product?.sku_id) {
        const company = await companyService.getCurrentCompany();
        if (company) {
          const companyIdNum = Number(company.id);
          const companySkuKey = `${companyIdNum}-${product.sku_id}`;
          
          if (costId) {
            // 更新现有记录
            const { error: costError } = await supabase
              .from('product_costs')
              .update({
                average_cost: averageCost,
                average_cost_date: new Date().toISOString()
              })
              .eq('id', costId);

            if (costError) {
              console.error('更新成本資料失敗:', costError);
              toast.error('更新成本資料失敗: ' + costError.message);
            }
          } else if (averageCost !== null) {
            // 创建新记录
            const { error: costError } = await supabase
              .from('product_costs')
              .insert({
                company_id: companyIdNum,
                sku_id: product.sku_id,
                company_sku_key: companySkuKey,
                average_cost: averageCost,
                average_cost_date: new Date().toISOString()
              });

            if (costError) {
              console.error('創建成本資料失敗:', costError);
              toast.error('創建成本資料失敗: ' + costError.message);
            }
          }
        }
      }

      toast.success('產品資料已成功更新');
    } catch (error: any) {
      toast.error('更新產品資料失敗: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateProduct = (field: keyof Product, value: any) => {
    if (product) {
      setProduct({ ...product, [field]: value });
    }
  };

  const getCategoryCodeDisplay = (code: string | null | undefined): string => {
    if (!code) return '';
    // 如果已经有查询到的描述信息，使用它；否则显示 code
    return categoryCodeDisplay || code;
  };

  const getDecodedHtmlOrPlaceholder = (value: string | null | undefined, placeholder: string): string => {
    const decoded = decodeHtmlEntities(value || '');
    return decoded.trim().length > 0 ? decoded : placeholder;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">產品不存在</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/product-overview/management')}
              className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回產品列表
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">編輯產品</h1>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* 產品資訊 */}
          <Card className="bg-white border-gray-200">
            <Collapsible open={isProductInfoOpen} onOpenChange={setIsProductInfoOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="bg-gray-50 border-b border-gray-200 flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    產品資訊
                  </CardTitle>
                  {isProductInfoOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="sku_id" className="text-gray-700 font-medium">SKU ID <span className="text-red-500">*</span></Label>
                      <Input
                        id="sku_id"
                        value={product.sku_id || ''}
                        onChange={(e) => updateProduct('sku_id', e.target.value)}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        maxLength={39}
                      />
                      <p className="text-xs text-gray-500 mt-1">{(product.sku_id || '').length} / 39</p>
                    </div>
                    <div>
                      <Label htmlFor="product_id" className="text-gray-700 font-medium">產品 ID <span className="text-red-500">*</span></Label>
                      <Input
                        id="product_id"
                        value={product.product_id || ''}
                        onChange={(e) => updateProduct('product_id', e.target.value)}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        maxLength={39}
                      />
                      <p className="text-xs text-gray-500 mt-1">{(product.product_id || '').length} / 39</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="product_type_code" className="text-gray-700 font-medium">產品類別代碼 <span className="text-red-500">*</span></Label>
                      <div className="flex gap-2 flex-wrap">
                        {productTypeCodes.length > 0 ? (
                          productTypeCodes.map((item, index) => (
                            <Badge key={index} variant="outline" className="bg-gray-100 text-gray-700 flex flex-col items-start py-2 px-3">
                              <span>{item.code} -</span>
                              <span>{item.description || ''}</span>
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">載入中...</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="category_code" className="text-gray-700 font-medium">主產品類別代碼 <span className="text-red-500">*</span></Label>
                      <Input
                        id="category_code"
                        value={getCategoryCodeDisplay(product.category_code)}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="brand" className="text-gray-700 font-medium">品牌名稱 (Brand) <span className="text-red-500">*</span></Label>
                      <Input
                        id="brand"
                        value={product.brand || ''}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="warehouse" className="text-gray-700 font-medium">倉庫 (Warehouse)</Label>
                      <Input
                        id="warehouse"
                        value={product.warehouse || ''}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="standard_commission" className="text-gray-700 font-medium">標準佣金率 (％)</Label>
                      <Input
                        id="standard_commission"
                        value={standardCommission !== null ? standardCommission.toString() : ''}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* 產品顯示列表 */}
          <Card className="bg-white border-gray-200">
            <Collapsible open={isSkuListOpen} onOpenChange={setIsSkuListOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="bg-gray-50 border-b border-gray-200 flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-900">產品顯示列表</CardTitle>
                  {isSkuListOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>產品條碼</TableHead>
                          <TableHead>主要SKU</TableHead>
                          <TableHead>SKU名稱 (英文)</TableHead>
                          <TableHead>SKU名稱 (繁體中文)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="text-blue-600 font-medium">{product.barcode || ''}</TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">是</span>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="line-clamp-3 break-words text-gray-900">
                              {decodeHtmlEntities(product.sku_name || '')}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <div className="line-clamp-3 break-words text-gray-900">
                              {decodeHtmlEntities(product.sku_name_chi || '')}
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    <div>
                      <Label htmlFor="original_price" className="text-gray-700 font-medium">原價 (港幣)</Label>
                      <Input
                        id="original_price"
                        value={product.original_price || ''}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="selling_price" className="text-gray-700 font-medium">售價 (港幣)</Label>
                      <Input
                        id="selling_price"
                        value={product.selling_price || ''}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="average_cost" className="text-gray-700 font-medium">成本 (港幣)</Label>
                      <Input
                        id="average_cost"
                        type="number"
                        value={averageCost || ''}
                        onChange={(e) => setAverageCost(e.target.value ? parseFloat(e.target.value) : null)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* SKU資訊 */}
          <Card className="bg-white border-gray-200">
            <Collapsible open={isSkuInfoOpen} onOpenChange={setIsSkuInfoOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="bg-gray-50 border-b border-gray-200 flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-900">SKU內容</CardTitle>
                  {isSkuInfoOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <Label htmlFor="sku_name_chi" className="text-gray-700 font-medium">SKU名稱 (繁體中文) <span className="text-red-500">*</span></Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_name_chi,
                            '<span class="text-gray-400">無內容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_name" className="text-gray-700 font-medium">SKU名稱 (英文) <span className="text-red-500">*</span></Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_name,
                            '<span class="text-gray-400">No content</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_name_sc" className="text-gray-700 font-medium">SKU名稱 (簡體中文) <span className="text-red-500">*</span></Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_name_sc,
                            '<span class="text-gray-400">无内容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_short_description_chi" className="text-gray-700 font-medium">SKU簡介 (繁體中文) <span className="text-red-500">*</span></Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_short_description_chi,
                            '<span class="text-gray-400">無內容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_short_description" className="text-gray-700 font-medium">SKU簡介 (英文) <span className="text-red-500">*</span></Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_short_description,
                            '<span class="text-gray-400">No content</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_short_description_sc" className="text-gray-700 font-medium">SKU簡介 (簡體中文) <span className="text-red-500">*</span></Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_short_description_sc,
                            '<span class="text-gray-400">无内容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="origin" className="text-gray-700 font-medium">產地 <span className="text-red-500">*</span></Label>
                      <Input
                        id="origin"
                        value={product.manufactured_country || ''}
                        className="mt-1 bg-gray-100 border-gray-300 text-gray-900"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="shelf_life" className="text-gray-700 font-medium">可退貨日 <span className="text-red-500">*</span></Label>
                      <Select>
                        <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder="0" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0</SelectItem>
                          <SelectItem value="7">7</SelectItem>
                          <SelectItem value="14">14</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* 包裝及尺寸 */}
          <Card className="bg-white border-gray-200">
            <Collapsible open={isPackagingOpen} onOpenChange={setIsPackagingOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="bg-gray-50 border-b border-gray-200 flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-900">包裝及尺寸</CardTitle>
                  {isPackagingOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="package_unit" className="text-gray-700 font-medium">包裝尺寸單位 <span className="text-red-500">*</span></Label>
                      <Select value={product.packing_dimension_unit || ''} onValueChange={(value) => updateProduct('packing_dimension_unit', value)}>
                        <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder="厘米" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cm">厘米</SelectItem>
                          <SelectItem value="mm">毫米</SelectItem>
                          <SelectItem value="m">米</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="package_length" className="text-gray-700 font-medium">包裝長度 <span className="text-red-500">*</span></Label>
                      <Input
                        id="package_length"
                        type="number"
                        value={product.packing_length || ''}
                        onChange={(e) => updateProduct('packing_length', e.target.value ? parseFloat(e.target.value) : null)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="30"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="package_width" className="text-gray-700 font-medium">包裝寬度 <span className="text-red-500">*</span></Label>
                      <Input
                        id="package_width"
                        type="number"
                        value={product.packing_depth || ''}
                        onChange={(e) => updateProduct('packing_depth', e.target.value ? parseFloat(e.target.value) : null)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="40"
                      />
                    </div>
                    <div>
                      <Label htmlFor="package_height" className="text-gray-700 font-medium">包裝高度 <span className="text-red-500">*</span></Label>
                      <Input
                        id="package_height"
                        type="number"
                        value={product.packing_height || ''}
                        onChange={(e) => updateProduct('packing_height', e.target.value ? parseFloat(e.target.value) : null)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="30"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="weight_unit" className="text-gray-700 font-medium">重量單位 <span className="text-red-500">*</span></Label>
                      <Select value={product.weight_unit || ''} onValueChange={(value) => updateProduct('weight_unit', value)}>
                        <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder="公斤" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">公斤</SelectItem>
                          <SelectItem value="g">克</SelectItem>
                          <SelectItem value="lb">磅</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="weight" className="text-gray-700 font-medium">重量 <span className="text-red-500">*</span></Label>
                      <Input
                        id="weight"
                        type="number"
                        value={product.weight || ''}
                        onChange={(e) => updateProduct('weight', e.target.value ? parseFloat(e.target.value) : null)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="2"
                      />
                      <p className="text-xs text-gray-500 mt-1">公斤</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="package_spec_chi" className="text-gray-700 font-medium">包裝規格 (繁體中文)</Label>
                      <Input
                        id="package_spec_chi"
                        value={product.packing_spec_chi || ''}
                        onChange={(e) => updateProduct('packing_spec_chi', e.target.value)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="2kg"
                        maxLength={20}
                      />
                      <p className="text-xs text-gray-500 mt-1">{(product.packing_spec_chi || '').length} / 20</p>
                    </div>
                    <div>
                      <Label htmlFor="package_spec_eng" className="text-gray-700 font-medium">包裝規格 (英文)</Label>
                      <Input
                        id="package_spec_eng"
                        value={product.packing_spec_eng || ''}
                        onChange={(e) => updateProduct('packing_spec_eng', e.target.value)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="2kg"
                        maxLength={20}
                      />
                      <p className="text-xs text-gray-500 mt-1">{(product.packing_spec_eng || '').length} / 20</p>
                    </div>
                    <div>
                      <Label htmlFor="package_spec_sc" className="text-gray-700 font-medium">包裝規格 (簡體中文)</Label>
                      <Input
                        id="package_spec_sc"
                        value={product.packing_spec_sc || ''}
                        onChange={(e) => updateProduct('packing_spec_sc', e.target.value)}
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="2公斤"
                        maxLength={20}
                      />
                      <p className="text-xs text-gray-500 mt-1">{(product.packing_spec_sc || '').length} / 20</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="size_cat" className="text-gray-700 font-medium">尺寸類別</Label>
                      <Select>
                        <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder="請選擇尺寸類別" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">小</SelectItem>
                          <SelectItem value="medium">中</SelectItem>
                          <SelectItem value="large">大</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="size" className="text-gray-700 font-medium">尺寸</Label>
                      <Select>
                        <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder="請選擇尺寸" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">小</SelectItem>
                          <SelectItem value="medium">中</SelectItem>
                          <SelectItem value="large">大</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="original_length" className="text-gray-700 font-medium">原料長度</Label>
                      <Input
                        id="original_length"
                        type="number"
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="請輸入數字，不可填小數點0"
                      />
                      <p className="text-xs text-gray-500 mt-1">mm</p>
                    </div>
                    <div>
                      <Label htmlFor="original_width" className="text-gray-700 font-medium">原料寬度</Label>
                      <Input
                        id="original_width"
                        type="number"
                        className="mt-1 bg-white border-gray-300 text-gray-900"
                        placeholder="請輸入數字，不可填小數點0"
                      />
                      <p className="text-xs text-gray-500 mt-1">mm</p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="original_height" className="text-gray-700 font-medium">原料高度</Label>
                    <Input
                      id="original_height"
                      type="number"
                      className="mt-1 bg-white border-gray-300 text-gray-900"
                      placeholder="請輸入數字，不可填小數點0"
                    />
                    <p className="text-xs text-gray-500 mt-1">mm</p>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* SKU詳細介紹 */}
          <Card className="bg-white border-gray-200">
            <Collapsible open={isSkuDetailOpen} onOpenChange={setIsSkuDetailOpen}>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="bg-gray-50 border-b border-gray-200 flex flex-row items-center justify-between">
                  <CardTitle className="text-gray-900">SKU詳細介紹 (繁體中文)</CardTitle>
                  {isSkuDetailOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <Label htmlFor="sku_long_description_chi" className="text-gray-700 font-medium">SKU詳細介紹 (繁體中文)</Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_long_description_chi,
                            '<span class="text-gray-400">無內容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_long_description" className="text-gray-700 font-medium">SKU詳細介紹 (英文)</Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_long_description,
                            '<span class="text-gray-400">No content</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="sku_long_description_sc" className="text-gray-700 font-medium">SKU詳細介紹 (簡體中文)</Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.sku_long_description_sc,
                            '<span class="text-gray-400">无内容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="fine_print_chi" className="text-gray-700 font-medium">條款與細則 (繁體中文)</Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.fine_print_chi,
                            '<span class="text-gray-400">無內容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="fine_print_eng" className="text-gray-700 font-medium">條款與細則 (英文)</Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.fine_print_eng,
                            '<span class="text-gray-400">No content</span>'
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="fine_print_sc" className="text-gray-700 font-medium">條款與細則 (簡體中文)</Label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm leading-relaxed break-words text-gray-900">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getDecodedHtmlOrPlaceholder(
                            product.fine_print_sc,
                            '<span class="text-gray-400">无内容</span>'
                          )
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProductEdit;